import {
  IdCardWrapper,
  TitleAndCommandsWrapper,
  TitlesWrapper,
  CommandsWrapper,
  SaveChangesExitWrapper,
  TextualDetailsWrapper,
  SectionTitle,
  SubMenusWrapper,
  SaveChangesButton,
  DiscardChangesButton,
  IdCardTitle,
  ModuleTitle,
  ModuleTitleAndInputWrapper,
  ModuleTitleInput,
  DropDownWrapper,
  DescriptionAndMainImageWrapper,
  NoFamiliesMsg,
} from './styles';

import DescriptionEdit from '../DescriptionEdit';
import CharacteristicsEdit from '../CharacteristicsEdit';
import PhysicalEdit from '../PhysicalEdit';
import SubMenuItem from '../SubMenuItem';
import Dropdown from '../Dropdown';
import ViewsAndLayoutsEdit from '../ViewsAndLayoutsEdit';
import WeightBreakdownEdit from '../WeightBreakdownEdit';
import LocationAndCraneCoverageEdit from '../LocationAndCraneCoverageEdit';
import MainTieInsEdit from '../MainTieInsEdit';
import EquipmentListEdit from '../EquipmentListEdit';
import DocumentationListEdit from '../DocumentationListEdit';
import OtherRelevantDocumentsEdit from '../OtherRelevantDocumentsEdit';
import ProjectNotesEdit from '../ProjectNotesEdit';
import useObjectElements from '../../hooks/useObjectElements';
import ModalView from '../ModalView';
import Loader from '../Loader';
import MainImageEdit from '../MainImageEdit';

import { defaultIdCard } from '../../config/defaultIdCard';
import useCreateIdCard from '../../hooks/useCreateIdCard';
import { createRef, useState } from 'react';
import useDeleteImage from '../../hooks/useDeleteImage';
import { ImageContext } from '../../context/ImageContext';
import FamiliesDropdown from '../FamiliesDropdown';
import SecondarySystemDropdown from '../SecondarySystemDropdown';
import ProjectsDropdown from '../ProjectsDropdown';
import ValidationInput from '../ValidationInput';
import { toast } from 'react-toastify';
import { Prompt } from 'react-router-dom';
import { MODAL_ACTION, DESIGN_MATURITY_OPTIONS } from '../../config/enums';
import { useIdCatalogSearch } from '../SideMenuSearchLane/context';

const IdCardCreate = ({ onModalClose }) => {
  const {
    object: idCardNewData,
    setObjectValue: setNewDataField,
    setMultipleObjectValues: setMultipleNewFields,
  } = useObjectElements({ initialValues: { ...defaultIdCard } });
  const [idCardImages, setIdCardImages] = useState(defaultIdCard.images);
  const { deleteImage } = useDeleteImage();
  const { createIdCard, isLoading: isLoadingCreate } = useCreateIdCard({ onModalClose });
  const [familyAccess, setFamilyAccess] = useState(true);
  const [projectAccess, setProjectAccess] = useState(true);
  const {
    searchFilter,
    updateSearchFilterField,
  } = useIdCatalogSearch();

  const onModalLeaveClick = (locationPath) => {
    setModalProp(MODAL_ACTION.IS_OPEN, false);
    deleteAllUnusedUploadedImages();
  };

  const onModalDiscardClick = () => {
    setModalProp(MODAL_ACTION.IS_OPEN, false);
    setModalProp(MODAL_ACTION.ON_RIGHT_BTN, () => onModalLeaveClick());
  };

  const {
    object: modalData,
    setObjectValue: setModalProp,
    setObject: setModalData,
  } = useObjectElements({
    initialValues: {
      modalIsOpen: false,
      modalTitle: 'Are you sure?',
      leftBtnTitle: 'NO',
      rightBtnTitle: 'YES',
      modalDescription: "Are you sure you don't want to save the ID Card?",
      onCloseModal: onModalDiscardClick,
      onBtnLeftClick: onModalDiscardClick,
      onBtnRightClick: () => onModalLeaveClick(),
    },
  });

  const onPageDiscardButton = () => {
    setModalProp(MODAL_ACTION.IS_OPEN, true);
  };

  const deleteAllUnusedUploadedImages = () => {
    // check for images that were uploaded but the changes weren't saved
    idCardImages.forEach((image) => {
      if (image.imageId) {
        deleteImage(image.imageId);
      }
    });
  };

  const validationFunc = (name, value) => {
    return value?.length > 1;
  };

  // mandatory fields refs

  const nameRef = createRef();
  const tsCodeRef = createRef();
  const descriptionRef = createRef();

  const onSaveCreatedIdCard = () => {
    if (!validationFunc('projectId', idCardNewData.projectId)) {
      toast.error(`Please select a project!`, {
        position: toast.POSITION.TOP_LEFT,
      });
      return;
    }
    if (!validationFunc('tsCode', idCardNewData.tsCode)) {
      tsCodeRef.current.scrollIntoView();
      tsCodeRef.current.focus();
      toast.error(`Please fill in a module number (e.g. TS032)`, {
        position: toast.POSITION.TOP_LEFT,
      });
      return;
    }
    if (!validationFunc('name', idCardNewData.name)) {
      nameRef.current.scrollIntoView();
      nameRef.current.focus();
      toast.error(`Please fill in a name`, {
        position: toast.POSITION.TOP_LEFT,
      });
      return;
    }
    if (!validationFunc('description', idCardNewData.description)) {
      descriptionRef.current.scrollIntoView();
      descriptionRef.current.focus();
      toast.error(`Please fill in a description`, {
        position: toast.POSITION.TOP_LEFT,
      });
      return;
    }
    const newIdCard = { ...idCardNewData, images: idCardImages };
    createIdCard({idCardData: newIdCard, onSuccessCallback: () => updateSearchFilterField('refreshToggle', !searchFilter.refreshToggle)});
  };

  const handleOnLeavePage = (location, action) => {
    if (action === 'POP') {
      return true;
    }
    if (modalData.modalIsOpen) {
      return true;
    }

    if (location?.state?.blockPopupModal) {
      return true;
    }

    if (location?.state?.from?.pathname === `/catalog//idcard/new/create`) {
      return true;
    }

    setModalData({
      modalIsOpen: true,
      modalTitle: 'Are you sure?',
      leftBtnTitle: 'NO',
      rightBtnTitle: 'YES',
      modalDescription: "Are you sure you don't want to save the ID Card?",
      onCloseModal: onModalDiscardClick,
      onBtnLeftClick: onModalDiscardClick,
      onBtnRightClick: () => onModalLeaveClick(location.pathname),
    });
    return false;
  };

  const onSuccessImageUpload = (resData, imageFile, imageArrIndex) => {
    const updatedImages = [...idCardImages];
    updatedImages[imageArrIndex] = {
      imageId: resData,
      indexNr: imageArrIndex,
      legend: imageFile.name,
    };
    setIdCardImages(updatedImages);
  };

  const onSuccessImageRemove = (resData, imageFile, imageArrIndex) => {
    const updatedImages = [...idCardImages];
    updatedImages[imageArrIndex] = {
      imageId: null,
      indexNr: imageArrIndex,
      legend: '',
    };
    setIdCardImages(updatedImages);
  };

  const shouldSendDeleteImageReq = (imageId) => {
    return !!imageId;
  };

  if (isLoadingCreate) {
    return <Loader />;
  }

  if (!familyAccess) {
    return (
      <NoFamiliesMsg>
        Please wait for family request to finish or reload.
        <br />
        You cannot create ID Card without selecting a family!
      </NoFamiliesMsg>
    );
  }

  if (!projectAccess) {
    return <NoFamiliesMsg>Loading projects...</NoFamiliesMsg>;
  }
  return (
    <ImageContext.Provider
      value={{
        idCardImages,
        setIdCardImages,
        onSuccessImageUpload,
        onSuccessImageRemove,
        shouldSendDeleteImageReq,
      }}>
      <IdCardWrapper>
        <Prompt
          when={true}
          message={(location, action) => {
            return handleOnLeavePage(location, action);
          }}
        />
        <ModalView
          isOpen={modalData.modalIsOpen}
          title={modalData.modalTitle}
          description={modalData.modalDescription}
          onCloseBtn={modalData.onCloseModal}
          onBtnLeftClick={modalData.onBtnLeftClick}
          onBtnRightClick={modalData.onBtnRightClick}
          leftBtnTitle={modalData.leftBtnTitle}
          rightBtnTitle={modalData.rightBtnTitle}
        />
        <TitleAndCommandsWrapper>
          <TitlesWrapper>
            <ModuleTitleAndInputWrapper>
              <IdCardTitle>Project</IdCardTitle>
              <DropDownWrapper>
                <ProjectsDropdown
                  projectId={idCardNewData.projectId}
                  setDataFunc={setMultipleNewFields}
                  setProjectAccess={setProjectAccess}
                />
              </DropDownWrapper>
            </ModuleTitleAndInputWrapper>
            <ModuleTitleAndInputWrapper>
              <ModuleTitle>Module:</ModuleTitle>
              <ValidationInput
                ref={tsCodeRef}
                component={ModuleTitleInput}
                containerStyle={{ width: '35%', height: '100%' }}
                placeholder='Module Number'
                value={idCardNewData.tsCode}
                onChange={(e) => setNewDataField('tsCode', e.target.value)}
                name='moduleNumber'
                validation={validationFunc}
                errorMsg={'Number must be longer than 1 char'}
              />
              <ValidationInput
                ref={nameRef}
                component={ModuleTitleInput}
                containerStyle={{ width: '65%', height: '100%' }}
                placeholder='Module Name'
                value={idCardNewData.name}
                onChange={(e) => setNewDataField('name', e.target.value)}
                name='moduleName'
                validation={validationFunc}
                errorMsg={'Name must be longer than 1 char'}
              />
            </ModuleTitleAndInputWrapper>
            <ModuleTitleAndInputWrapper>
              <ModuleTitle>Family:</ModuleTitle>
              <DropDownWrapper>
                <FamiliesDropdown
                  familyId={idCardNewData.familyId}
                  setDataFunc={setMultipleNewFields}
                  setFamilyAccess={setFamilyAccess}
                />
              </DropDownWrapper>
            </ModuleTitleAndInputWrapper>
            <ModuleTitleAndInputWrapper>
              <ModuleTitle>Secondary system:</ModuleTitle>
              <DropDownWrapper>
                <SecondarySystemDropdown
                  setDataFunc={setMultipleNewFields}
                  value={idCardNewData.secondarySystem}
                />
              </DropDownWrapper>
            </ModuleTitleAndInputWrapper>
            <ModuleTitleAndInputWrapper>
              <ModuleTitle>Design Maturity:</ModuleTitle>
              <DropDownWrapper>
                <Dropdown
                  value={idCardNewData.designMaturity}
                  options={DESIGN_MATURITY_OPTIONS}
                  placeholder='Design Maturity'
                  onChange={(option) => {
                    setNewDataField('designMaturity', option.value);
                  }}
                />
              </DropDownWrapper>
            </ModuleTitleAndInputWrapper>
          </TitlesWrapper>
          <CommandsWrapper>
            <SaveChangesExitWrapper>
              <SaveChangesButton
                title={'Save'}
                onClick={() => onSaveCreatedIdCard()}
              />
            </SaveChangesExitWrapper>
            <DiscardChangesButton
              type='transparentBackgroundButton'
              title='Cancel'
              onClick={onPageDiscardButton}
            />
          </CommandsWrapper>
        </TitleAndCommandsWrapper>

        <TextualDetailsWrapper>
          <SectionTitle>Description:</SectionTitle>
          <DescriptionAndMainImageWrapper>
            <DescriptionEdit
              ref={descriptionRef}
              text={idCardNewData.description}
              onChange={(e) => setNewDataField('description', e.target.value)}
            />
            <MainImageEdit
              image={idCardImages[0]}
              imageIndex={0}
            />
          </DescriptionAndMainImageWrapper>
          <SectionTitle>
            {idCardNewData.characteristicsLabel && idCardNewData.characteristicsLabel.length > 0
              ? idCardNewData.characteristicsLabel
              : 'Characteristics:'}
          </SectionTitle>
          <CharacteristicsEdit
            characteristics={idCardNewData.characteristics}
            onChange={(e) => {
              let updatedCharacteristic = idCardNewData.characteristics[e.target.name];
              updatedCharacteristic.value = e.target.value;
              idCardNewData.characteristics.splice(e.target.name, 1, updatedCharacteristic);
              setNewDataField('characteristics', idCardNewData.characteristics);
            }}
          />

          <SectionTitle>Physical:</SectionTitle>
          <PhysicalEdit
            totalWeightDry={idCardNewData.totalWeightDry}
            length={idCardNewData.length}
            width={idCardNewData.width}
            height={idCardNewData.height}
            onChange={(e) => setNewDataField(e.target.name, e.target.value)}
          />
        </TextualDetailsWrapper>

        <SubMenusWrapper>
          <SubMenuItem
            title={'Views And Layouts'}
            component={<ViewsAndLayoutsEdit images={idCardImages.slice(1, 7)} />}
          />
          <SubMenuItem
            title={'Weight Breakdown'}
            component={
              <WeightBreakdownEdit
                tableData={{
                  weightStructure: idCardNewData.weightStructure,
                  weightMechanical: idCardNewData.weightMechanical,
                  weightPiping: idCardNewData.weightPiping,
                  weightElectrical: idCardNewData.weightElectrical,
                  weightInstrument: idCardNewData.weightInstrument,
                  totalWeightDry: idCardNewData.totalWeightDry,
                  totalWeightFluids: idCardNewData.totalWeightFluids,
                  totalWeightOperating: idCardNewData.totalWeightOperating,
                }}
                setEditedDataField={setNewDataField}
              />
            }
          />
          <SubMenuItem
            title={'Location on FPSO'}
            component={<LocationAndCraneCoverageEdit image={idCardImages[8]} />}
          />
          <SubMenuItem
            title={'Main Tie-Ins & Interfaces'}
            component={
              <MainTieInsEdit
                title={idCardNewData.tieInsComment}
                tableData={idCardNewData.tieIns}
                setEditedDataField={setNewDataField}
              />
            }
          />
          <SubMenuItem
            title={'Key Equipment List'}
            component={
              <EquipmentListEdit
                title={idCardNewData.equipmentListComment}
                tableData={idCardNewData.equipmentList}
                setEditedDataField={setNewDataField}
              />
            }
          />
          <SubMenuItem
            title={'Documentation List'}
            component={
              <DocumentationListEdit
                tableData={idCardNewData.documents}
                setEditedDataField={setNewDataField}
              />
            }
          />
          <SubMenuItem
            title={'Other Relevant Documents'}
            component={
              <OtherRelevantDocumentsEdit
                tableData={idCardNewData.otherDocuments}
                setEditedDataField={setNewDataField}
              />
            }
          />
          <SubMenuItem
            title={'Project Notes'}
            component={
              <ProjectNotesEdit
                notes={idCardNewData.resNotes}
                setEditedDataField={setNewDataField}
              />
            }
          />
        </SubMenusWrapper>
      </IdCardWrapper>
    </ImageContext.Provider>
  );
};

export default IdCardCreate;
